import type { ReactNode } from "react";
import { Lettering } from "../../ui/primitives.js";

/**
 * The Storyteller's panel furniture: a 35px heading with a small uppercase
 * label, and the page control that sits under every reading area.
 *
 * Both are deliberately tiny. The composition gives the panels almost all of
 * the screen, so everything that is not the story, the scene or the paper has
 * to earn its pixels — a label, a count, two arrows.
 */

export function PanelHeading({ label, children, tools }: { label: string; children?: ReactNode; tools?: ReactNode }): ReactNode {
  return (
    <div className="pcs-heading">
      <span className="pcs-label">{label}</span>
      {children === undefined ? null : <span className="pcs-quiet pcs-truncate">{children}</span>}
      {/* Controls at the heading's right hand (the front door's desk tools, 2026-10-31): rendered as given, not as a quiet aside. */}
      {tools === undefined ? null : tools}
    </div>
  );
}

/**
 * Previous / next / where you are. The count is the honest one — the page set
 * the measuring column produced — so "3 / 7" means there are seven pages and
 * the seventh really is the end of the content.
 */
export function Pager({
  page,
  count,
  onPage,
  what,
  end,
}: {
  readonly page: number;
  readonly count: number;
  readonly onPage: (next: number) => void;
  /** What is being paged, for the screen reader: "story", "document", "papers". */
  readonly what: string;
  readonly end?: ReactNode;
}): ReactNode {
  return (
    <div className="pcs-pager">
      <button
        type="button"
        className="pcs-pager-arrow"
        onClick={() => { onPage(page - 1); }}
        disabled={page <= 0}
        aria-label={`Previous ${what} page`}
      >
        <Lettering text="←" />
      </button>
      <span aria-hidden="true">
        {String(page + 1)} / {String(count)}
      </span>
      <span className="sr-only">
        {what} page {String(page + 1)} of {String(count)}
      </span>
      <button
        type="button"
        className="pcs-pager-arrow"
        onClick={() => { onPage(page + 1); }}
        disabled={page >= count - 1}
        aria-label={`Next ${what} page`}
      >
        <Lettering text="→" />
      </button>
      {end === undefined ? null : <span className="pcs-pager-end">{end}</span>}
    </div>
  );
}
